import { Service } from '@toeverything/infra';
import { type EditorContentInfo } from '../types/chat-message.d';
import type { BaseSelection, BlockModel } from '@blocksuite/affine/store';
import type {
  AgentBetaStore,
  ChatConfigType,
} from '../stores/agent-store-beta';
import type { AgentStoreService } from './agent-store-service';
import type { OrganizeService } from '../../organize';
import { WorkspaceService } from '../../workspace'; // 添加导入
import {
  defaultImageProxyMiddleware,
  docLinkBaseURLMiddleware,
  MixTextAdapter,
} from '@blocksuite/affine/shared/adapters';

export class AgentServiceBeta extends Service {
  constructor(
    private readonly _agentBetaStore: AgentBetaStore,
    private readonly _agentStoreService: AgentStoreService,
    private readonly _organizeService: OrganizeService
  ) {
    super();
    (
      window as unknown as Window & { __agentServiceBeta: AgentServiceBeta }
    ).__agentServiceBeta = this;
  }

  updateChatType(type: 'home' | 'doc') {
    this._updateChatConfig({
      type,
    });
  }

  setEditorDisplaySelections(selections: BaseSelection[]) {
    this._agentBetaStore.setEditorDisplaySelections(selections);
  }

  /**
   * 获取当前编辑选中 & 全文信息
   * 通过 AgentBetaStore 代理获取，保持接口兼容性
   */
  async getEditorContent(): Promise<EditorContentInfo> {
    return new Promise((resolve, reject) => {
      if (this._agentBetaStore) {
        this._agentBetaStore.getEditorContent().then(data => {
          resolve(data as EditorContentInfo);
        }, reject);
      } else {
        reject('agentBetaStore not found');
      }
    });
  }

  /**
   * 消费 LLM 命令
   * 处理来自 AI Agent 的命令，包括创建文档和更新内容
   *
   * @param complexLLMCommand - LLM 命令对象
   * @param complexLLMCommand.doc_id - 文档 ID
   * @param complexLLMCommand.snap_content - Markdown 快照内容
   * @param complexLLMCommand.title_new_value - 新标题（可选）
   * @param complexLLMCommand.parent_id - 父文档 ID（可选）
   * @param complexLLMCommand.command - 命令类型：create/update/insert/delete
   * @param complexLLMCommand.uuid - 对话 ID
   */
  async consumeLLMCommand(complexLLMCommand: {
    doc_id: string;
    snap_content: string;
    title_new_value?: string;
    parent_id?: string;
    command: 'update' | 'insert' | 'delete' | 'create';
    uuid: string;
  }): Promise<void> {
    //
    console.log('####consumeLLMCommand', complexLLMCommand);

    if (complexLLMCommand.command === 'create') {
      return this._createDocOp({
        command: complexLLMCommand,
        doc_id: complexLLMCommand.doc_id,
        conversion_id: complexLLMCommand.uuid,
      }).catch(() => {
        // Error already logged in _createDocOp
      });
    }

    // 扫光
    this._organizeService.enableShimmer(complexLLMCommand.doc_id);

    // ✅ 使用 agentStoreService 创建 record
    this._agentStoreService
      .createRecordByDocId(complexLLMCommand.doc_id, 'complex', {
        docBaseDataSnapshot: {
          snapshot: null,
          markdown: '',
          blockLineMapping: [],
          docId: '',
        },
        conversion_id: complexLLMCommand.uuid,
        commands: [],
      })
      .catch(e => {
        console.error('Error creating agent record:', e);
      });

    // 更新标题
    if (complexLLMCommand.title_new_value) {
      this._organizeService
        .updateDocTitle(
          complexLLMCommand.doc_id,
          complexLLMCommand.title_new_value
        )
        .catch(e => {
          console.error('Error updating doc title:', e);
        });
    }

    // ✅ 使用 agentStoreService 更新 commands
    this._agentStoreService
      .updateCommands({
        doc_id: complexLLMCommand.doc_id,
        conversion_id: complexLLMCommand.uuid,
        commands: [complexLLMCommand],
      })
      .catch(e => {
        console.error('Error updating commands:', e);
      });
  }

  /**
   * 切换聊天关闭状态
   * @param type - 关闭类型，'end' 表示结束对话
   */
  toggleChatClose(type: string) {
    switch (type) {
      case 'end':
        // 重置 hasCreated
        this._updateChatConfig({
          hasCreated: false,
        });
        break;
    }
  }

  /**
   * 处理 generate_doc 指令（内部方法）
   * 创建新文档并执行初始化操作
   *
   * @private
   */
  private async _createDocOp(params: {
    command: {
      doc_id: string;
      snap_content: string;
      title_new_value?: string;
      parent_id?: string;
      command: 'update' | 'insert' | 'delete' | 'create';
      uuid: string;
    };
    doc_id: string;
    conversion_id: string;
  }) {
    const config = this._agentBetaStore.chatConfig$.getValue();
    // 先更新默认成功
    this._updateChatConfig({
      hasCreated: true,
    });
    return this._organizeService
      .createArticleWithId({
        options: {
          title: params.command.title_new_value,
          doc_id: params.command.doc_id,
          parentDocId:
            params.command.parent_id === 'root'
              ? undefined
              : params.command.parent_id, // root 默认不传递
        },
        isFirstCreate: !config.hasCreated,
      })
      .then(async doc => {
        //
        console.log('####doc', doc);
        this._updateChatConfig({
          hasCreated: true,
        });

        this._organizeService.enableShimmer(doc.id);

        // ✅ 使用 agentStoreService 新建 record
        await this._agentStoreService.createRecordByDocId(doc.id, 'create', {
          docBaseDataSnapshot: {
            snapshot: null,
            markdown: '',
            blockLineMapping: [],
            docId: '',
          },
          commands: [],
          conversion_id: params.conversion_id,
        });

        // ✅ 使用 agentStoreService 更新 commands
        await this._agentStoreService.updateCommands({
          doc_id: doc.id,
          conversion_id: params.conversion_id,
          commands: [
            {
              ...params.command,
              doc_id: doc.id,
            },
          ],
        });
      })
      .catch(() => {
        // 如果当前文章新建是失败，释放掉首次打开的状态
        if (!config.hasCreated) {
          this._updateChatConfig({
            hasCreated: false,
          });
        }
      });
  }

  private _updateChatConfig(config: Partial<ChatConfigType>) {
    const currentConfig = this._agentBetaStore.chatConfig$.getValue();
    this._agentBetaStore.chatConfig$.next({
      ...currentConfig,
      ...config,
    });
  }

  /**
   * 监听编辑器选区变化
   * @param cb - 回调函数，当选区变化时触发
   */
  observeSelection(
    cb: (event: {
      selectedModels: BlockModel[];
      selectionMarkdown: string;
      timestamp: number;
      start_line_number?: number;
    }) => void
  ) {
    this._agentBetaStore.observeSelectionChanged(cb);
  }

  /**
   * 从当前编辑器状态计算选区 markdown 和绝对行号，
   * 并通过 observeSelection 的回调链分发事件。
   *
   * 主要用于 table cell / inline 选区变化时触发，
   * 避免在局部组件中自行计算全文行号。
   */
  async emitSelectionFromEditor(overrides?: {
    selectionMarkdown?: string;
    relativeStartLine?: number;
  }) {
    try {
      const content = await this.getEditorContent();
      const selection = content.selection ?? {
        markdown: '',
        start_line_number: 0,
      };

      let startLine = selection.start_line_number ?? 0;
      if (
        overrides?.relativeStartLine &&
        overrides.relativeStartLine > 0 &&
        startLine > 0
      ) {
        startLine = startLine + (overrides.relativeStartLine - 1);
      }

      const event = {
        selectedModels: [] as BlockModel[],
        selectionMarkdown:
          overrides?.selectionMarkdown ?? selection.markdown ?? '',
        timestamp: Date.now(),
        start_line_number: startLine,
      };

      this._agentBetaStore.selectionChangedSubscribe.forEach(cb => cb(event));
    } catch (error) {
      console.warn('emitSelectionFromEditor failed:', error);
    }
  }

  /**
   * 校验并执行 chatContent 命令
   * 用于测试和规范化命令执行
   */
  async waitForFullSync() {
    const workspaceService = this.framework.get(WorkspaceService);
    // TODO  blob等附件没有保证同步
    await workspaceService.workspace.engine.doc.waitForSynced();
  }

  // 校验 chatContent 是否合法
  async specsChatCommand(specsContent: {
    doc_id: string;
    snap_content: string;
    title_new_value?: string;
    parent_id?: string;
    command: 'update' | 'insert' | 'delete' | 'create';
    uuid: string;
  }) {
    console.log('####specs-specsContent', specsContent);

    const { doc_id, snap_content, title_new_value, parent_id, command } =
      specsContent;

    if (command === 'create') {
      await this._organizeService.createArticleWithId({
        options: {
          title: title_new_value,
          doc_id,
          parentDocId: parent_id === 'root' ? undefined : parent_id, // root 默认不传递
        },
        isFirstCreate: false,
      });
    }

    const workspaceService = this.framework.get(WorkspaceService);
    await this.forceOverwriteDocWithMarkdown(
      workspaceService,
      doc_id,
      snap_content,
      title_new_value
    );

    return true;
  }

  /**
   * ｜｜ TODO 拆分单独的类来实现这个功能，将所有对 doc 进行操作的提取到一个类中，将所有doc markdown 查询的封装到一个类中
   * 通过 doc_id 全量使用 markdown 强行覆盖 doc 的函数
   * @param workspaceService - 工作区服务实例
   * @param docId - 要覆盖的文档 ID
   * @param markdown - 要覆盖的 markdown 内容
   * @param title - 可选的文档标题
   * @returns Promise<string> - 返回文档 ID
   * @throws Error - 当文档不存在或操作失败时抛出错误
   */
  async forceOverwriteDocWithMarkdown(
    workspaceService: WorkspaceService,
    docId: string,
    markdown: string,
    title?: string
  ): Promise<string> {
    const workspace = workspaceService.workspace;
    const collection = workspace.docCollection;

    // 获取目标文档
    const doc = collection.getDoc(docId)?.getStore();
    if (!doc) {
      throw new Error(`Document with ID ${docId} not found`);
    }

    try {
      // 获取页面块（affine:page）
      const pageBlocks = doc.getBlocksByFlavour('affine:page');
      if (pageBlocks.length === 0) {
        throw new Error('No page block found in the document');
      }

      const pageBlock = pageBlocks[0];

      // 添加新的 note 块
      const noteBlockId = doc.addBlock('affine:note', {}, pageBlock.id);

      const transformer = doc.getTransformer([
        defaultImageProxyMiddleware,
        docLinkBaseURLMiddleware(doc.workspace.id),
      ]);
      const assetsManager = transformer.assetsManager;

      await this._populateMarkdownAssetMap(
        markdown,
        workspaceService,
        assetsManager.getPathBlobIdMap(),
        assetsManager.getAssets()
      );

      const mixAdapter = new MixTextAdapter(transformer, doc.provider);
      const sliceSnapshot = await mixAdapter.toBlockSnapshotInSliceStruct({
        file: markdown,
        assets: assetsManager,
        workspaceId: doc.workspace.id,
        pageId: doc.id,
      });

      if (!sliceSnapshot) {
        throw new Error('Failed to parse markdown content.');
      }

      const blockSnapshots =
        sliceSnapshot.content.flatMap(block => block.children) ?? [];

      // 删除页面块的所有子块（完全清空文档内容）
      pageBlock.model.children.forEach(child => {
        if (child.id !== noteBlockId) {
          doc.deleteBlock(child);
        }
      });

      for (let i = 0; i < blockSnapshots.length; i++) {
        await transformer.snapshotToBlock(
          blockSnapshots[i],
          doc,
          noteBlockId,
          i
        );
      }

      // 如果提供了标题，更新文档标题
      if (title) {
        const titleDelta = {
          '$blocksuite:internal:text$': true,
          delta: [
            {
              insert: title,
            },
          ],
        };
        // @ts-expect-error - BlockModel 类型定义不完整，但 title 属性在运行时存在
        pageBlock.model.title = titleDelta;
      }

      return doc.id;
    } catch (error) {
      console.error('Failed to overwrite document with markdown:', error);
      throw new Error(
        `Failed to overwrite document: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  private async _populateMarkdownAssetMap(
    markdown: string,
    workspaceService: WorkspaceService,
    pathBlobIdMap: Map<string, string>,
    assetsMap: Map<string, Blob>
  ) {
    const imagePaths = this._extractMarkdownImagePaths(markdown);
    if (imagePaths.length === 0) {
      return;
    }

    const workspace = workspaceService.workspace;
    const engine = workspace.engine;
    if (!engine.started) {
      try {
        engine.start();
      } catch (error) {
        console.warn(
          'Failed to start workspace engine when mapping assets',
          error
        );
        return;
      }
    }

    const blobFrontend = engine.blob;
    const checkedBlobs = new Map<string, Blob | null>();

    for (const path of imagePaths) {
      const normalized = this._normalizeAssetPath(path);
      if (!normalized) {
        continue;
      }

      const { raw, clean } = normalized;
      const candidateIds = this._generateCandidateBlobIds(clean);
      let matchedBlobId: string | null = null;
      let matchedBlob: Blob | null = null;

      for (const candidateId of candidateIds) {
        if (!candidateId) {
          continue;
        }
        let cached = checkedBlobs.get(candidateId);
        if (cached === undefined) {
          cached = null;
          try {
            const record = await blobFrontend.get(candidateId);
            if (record) {
              const type =
                record.mime && record.mime !== ''
                  ? record.mime
                  : 'application/octet-stream';
              const fileName =
                this._inferFileName(clean, candidateId, type) ?? candidateId;
              const blobSource = new Uint8Array(record.data);
              cached =
                typeof File !== 'undefined'
                  ? new File([blobSource], fileName, { type })
                  : new Blob([blobSource], { type });
            }
          } catch (error) {
            console.warn(
              `Failed to check blob existence for ${candidateId}`,
              error
            );
          }
          checkedBlobs.set(candidateId, cached ?? null);
        }

        if (cached) {
          matchedBlobId = candidateId;
          matchedBlob = cached;
          break;
        }
      }

      if (!matchedBlobId || !matchedBlob) {
        continue;
      }

      assetsMap.set(matchedBlobId, matchedBlob);

      for (const key of this._generateMappingKeys(raw, clean)) {
        if (key) {
          pathBlobIdMap.set(key, matchedBlobId);
        }
      }
    }
  }

  private _inferFileName(
    path: string,
    fallbackId: string,
    mime: string
  ): string | null {
    const segments = path.split('/');
    const lastSegment = segments[segments.length - 1];
    if (lastSegment && !lastSegment.endsWith('/')) {
      return lastSegment;
    }

    const extension = mime.split('/').pop();
    if (extension) {
      return `${fallbackId}.${extension}`;
    }

    return fallbackId;
  }

  private _extractMarkdownImagePaths(markdown: string): string[] {
    const urlSet = new Set<string>();
    const imageRegex = /!\[[^\]]*?\]\(([^)]+)\)/g;
    const htmlImgRegex = /<img[^>]+src=['"]([^'"]+)['"][^>]*>/gi;

    let match: RegExpExecArray | null;
    while ((match = imageRegex.exec(markdown)) !== null) {
      const raw = match[1]?.trim();
      if (!raw) continue;
      urlSet.add(raw);
    }

    while ((match = htmlImgRegex.exec(markdown)) !== null) {
      const raw = match[1]?.trim();
      if (!raw) continue;
      urlSet.add(raw);
    }

    return Array.from(urlSet);
  }

  private _normalizeAssetPath(
    path: string
  ): { raw: string; clean: string } | null {
    const trimmed = path.replace(/^['"]|['"]$/g, '').trim();
    if (!trimmed) {
      return null;
    }

    const [withoutQuery] = trimmed.split(/[?#]/);
    if (!withoutQuery) {
      return null;
    }

    if (this._isExternalUrl(withoutQuery)) {
      return null;
    }

    let decoded = withoutQuery;
    try {
      decoded = decodeURIComponent(withoutQuery);
    } catch {
      decoded = withoutQuery;
    }

    const cleaned = decoded
      .replace(/^(?:\.\.\/|\.\/)+/, '')
      .replace(/^\/+/, '');

    return {
      raw: withoutQuery,
      clean: cleaned,
    };
  }

  private _isExternalUrl(path: string): boolean {
    return /^(?:[a-zA-Z][a-zA-Z0-9+.-]*:|\/\/)/.test(path);
  }

  private _generateCandidateBlobIds(path: string): string[] {
    const cleaned = path.replace(/^(?:\.\.\/|\.\/)+/, '').replace(/^\/+/, '');
    const candidates = new Set<string>();

    const addCandidate = (value: string) => {
      if (!value) return;
      const normalized = value.trim();
      if (!normalized) return;
      if (/^[A-Za-z0-9_-]{8,}={0,2}$/.test(normalized)) {
        candidates.add(normalized);
      }
    };

    const withoutExt = cleaned.replace(/\.[^.\\/]+$/, '');
    addCandidate(withoutExt);

    const segments = cleaned.split('/');
    if (segments.length > 0) {
      const lastSegment = segments[segments.length - 1] ?? '';
      addCandidate(lastSegment.replace(/\.[^.]+$/, ''));
      addCandidate(lastSegment);
    }

    const assetsMatch = cleaned.match(/assets\/([^/]+)/i);
    if (assetsMatch?.[1]) {
      addCandidate(assetsMatch[1].replace(/\.[^.]+$/, ''));
    }

    return Array.from(candidates);
  }

  private _generateMappingKeys(raw: string, clean: string): string[] {
    const keys = new Set<string>();
    const add = (value: string | null | undefined) => {
      if (!value) return;
      const trimmed = value.trim();
      if (trimmed) {
        keys.add(trimmed);
      }
    };

    add(raw);
    add(clean);

    const noLeadingSlash = clean.replace(/^\/+/, '');
    add(noLeadingSlash);

    const noRelativeSegments = noLeadingSlash.replace(/^(?:\.\.\/|\.\/)+/, '');
    add(noRelativeSegments);

    const filename = noRelativeSegments.split('/').pop();
    add(filename);
    if (filename) {
      add(filename.replace(/\.[^.]+$/, ''));
    }

    return Array.from(keys);
  }
}
